import {
  CATEGORY_COLORS,
  TRIP_REUSE_HOURS,
  UNITS,
  chainProfile,
  findReusableTrip,
  guessCategory,
  fragtNachListe,
  guessShop,
  type ShopKey,
  guessStaple,
  listeFuerLaden,
  learnCategoryOrder,
  learningRateFor,
  lowStock,
  matchProduct,
  normalizeName,
  observedCategoryOrder,
  parseItemInput,
  planActorRepair,
  planAdoption,
  planFamilyRepair,
  planFinishTrip,
  planReceipt,
  planWeekToList,
  restockAfterTrip,
  splitItemInput,
  suggestProducts,
  type CategoryKey,
  type ChainKey,
  type Family,
  type FamilyScopedRow,
  type Invite,
  type Item,
  type List,
  type LowStock,
  type MealPlanEntry,
  type Member,
  type OutboxEntry,
  type OutboxOperation,
  type PantryItem,
  type PantryLocation,
  type PartnerConnection,
  type PriceEntry,
  type Product,
  type Recipe,
  type RecipeDraft,
  type RecipeIngredient,
  type ResolvedResult,
  type Store,
  type SyncedRow,
  type SyncedTable,
  type Receipt,
  type Trip,
  type TripItem,
  type Unit,
  type WeekPlanResult,
} from "@ainkauf/core";
import { db, newId, nowIso, type PhotoQueueEntry } from "./dexie";
import { verkleinern } from "@/lib/image";
import { activeFamilyId, activeUserId, adoptionDone, localFamilyId, localMemberId, markAdoptionDone, setActiveIdentity } from "@/lib/session";
import { outboxGeaendert } from "@/lib/sync-events";

/**
 * Jede Änderung schreibt die Zeile lokal und legt im selben Schritt einen
 * Outbox-Eintrag an (Regel 1). Beides in einer Transaktion, damit es keinen
 * Zustand gibt, in dem eine Änderung sichtbar ist, aber nie übertragen wird.
 */
async function writeWithOutbox<T extends SyncedRow>(
  table: SyncedTable,
  row: T,
  operation: OutboxOperation = "upsert",
): Promise<void> {
  const entry: OutboxEntry<T> = {
    id: newId(),
    table,
    operation,
    row_id: row.id,
    payload: row,
    client_updated_at: row.client_updated_at,
    attempts: 0,
    next_attempt_at: row.client_updated_at,
    last_error: null,
  };

  await db.transaction("rw", db.table(table), db.outbox, async () => {
    await db.table(table).put(row);
    await db.outbox.put(entry as OutboxEntry);
  });

  // Erst wenn die Zeile sicher liegt: Der Runner lädt sofort hoch, statt auf
  // den nächsten Tick zu warten.
  outboxGeaendert();
}

async function warteAufListe(hoechstensMs: number): Promise<List | null> {
  const frist = Date.now() + hoechstensMs;
  while (Date.now() < frist) {
    const liste = await defaultList();
    if (liste) return liste;
    await new Promise((weiter) => setTimeout(weiter, 250));
  }
  return null;
}

/** Nur lesen. Live-Queries von Dexie laufen in einer Nur-Lese-Transaktion. */
/**
 * Die Standardliste der Familie.
 *
 * Bis zum 20.09.2026 war das die erste Zeile, die Dexie gerade herausgab,
 * ohne Blick auf is_default: Bei mehreren Listen war es Zufall, welche galt.
 * Jetzt zählt is_default, dann die Reihenfolge, dann der Name; und wenn gar
 * keine als Standard markiert ist, die erste nach derselben Regel.
 */
export async function defaultList(): Promise<List | null> {
  const rows = (await db.lists.where("family_id").equals(activeFamilyId()).toArray())
    .filter((row) => row.deleted_at === null)
    .sort((a, b) => a.sort_index - b.sort_index || a.name.localeCompare(b.name, "de-DE"));
  return rows.find((row) => row.is_default) ?? rows[0] ?? null;
}

/**
 * Die Standardliste, notfalls neu angelegt.
 *
 * Angemeldet und noch nie Listen geholt: warten, also null. Die Liste der
 * Familie liegt auf dem Server, create_family legt sie an, und sie kommt mit
 * dem ersten Abgleich. Wer hier sofort anlegt, hat danach zwei Listen
 * "Einkauf", und zwei Geräte zeigen verschiedene. Genau das ist der Familie
 * Doege beim zweiten Telefon passiert. Wer trotzdem sofort eine braucht,
 * etwa weil gerade jemand tippt, sagt das mit `auchVorErstemAbgleich`.
 */
export async function ensureDefaultList(auchVorErstemAbgleich = false): Promise<List | null> {
  const family_id = activeFamilyId();
  const existing = await defaultList();
  if (existing) return existing;

  if (family_id !== localFamilyId()) {
    const marke = await db.cursors.get("lists");
    if (!marke || marke.updated_at === null) {
      if (!auchVorErstemAbgleich) return null;
      // Jemand tippt schon, bevor der erste Abgleich durch ist. Ein paar
      // Sekunden warten kostet nichts, eine zweite Liste kostet Ordnung.
      const gewartet = await warteAufListe(5_000);
      if (gewartet) return gewartet;
    }
  }

  const timestamp = nowIso();
  const list: List = {
    id: newId(),
    family_id,
    name: "Einkauf",
    store_id: null,
    is_default: true,
    sort_index: 0,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("lists", list);
  return list;
}

export async function addItem(listId: string, raw: string): Promise<Item | null> {
  const input = raw.trim();
  if (input.length === 0) return null;

  const parsed = parseItemInput(input);
  const timestamp = nowIso();

  // Erst der Katalog, dann der Artikel: nur so trägt der Artikel von Anfang an
  // die Verknüpfung zum Produkt, und Kategorie und Einheit kommen von dort,
  // statt dass jemand sie erneut eintippt.
  const product = await rememberProduct(parsed.name, parsed.normalizedName, parsed.unit);

  const item: Item = {
    id: newId(),
    family_id: activeFamilyId(),
    list_id: listId,
    product_id: product.id,
    name: parsed.name,
    normalized_name: parsed.normalizedName,
    quantity: parsed.quantity,
    unit: parsed.unit ?? product.default_unit,
    note: parsed.note,
    category: product.category,
    is_checked: false,
    checked_at: null,
    checked_by: null,
    is_recurring: false,
    unavailable_at: null,
    photo_path: null,
    only_on_sale: false,
    created_by: activeUserId(),
    sort_index: -Date.parse(timestamp),
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("items", item);
  return item;
}

/**
 * Legt an, was in einer Eingabe steckt: einen Artikel oder mehrere.
 *
 * Der Weg für die Schnelleingabe. In das Feld wird auch diktiert, und dann
 * steht dort ein ganzer Satz. Bis zum 20.09.2026 wurde daraus ein Artikel
 * "Äpfel" mit der Notiz "Bananen, Milch und Klopapier", weil der Parser alles
 * nach dem ersten Komma als Notiz nimmt. Das Zerlegen steht im Kern und gilt
 * für das Feld wie für den Sprachassistenten.
 */
export async function addItems(listId: string, raw: string): Promise<Item[]> {
  const angelegt: Item[] = [];
  for (const teil of splitItemInput(raw)) {
    const item = await addItem(listId, teil);
    if (item) angelegt.push(item);
  }
  return angelegt;
}

/** Was beim Anlegen mit Verteilung herauskam, für die Rückmeldung. */
export interface SortiertesErgebnis {
  angelegt: Item[];
  /** Artikel, die in einer anderen Liste gelandet sind. */
  umgeleitet: { name: string; liste: string }[];
  /** Artikel, für die es keine passende Liste gibt. Der Aufrufer fragt nach. */
  ohneListe: { name: string; shop: ShopKey; raw: string }[];
}

/**
 * Artikel anlegen und dabei in die passende Liste einsortieren.
 *
 * Daniel am 03.10.2026: "Wenn ich Schrauben eingebe, sollten die Schrauben
 * automatisch in der Baumarktliste sein." Dasselbe für asiatische Zutaten
 * und die Drogerie.
 *
 * **Verteilt wird nur aus der Standardliste.** Wer die Liste "Asiamarkt"
 * gezielt öffnet und dort etwas einträgt, meint sie auch; ihm die Zeile
 * wegzunehmen wäre Bevormundung. Aus dem Supermarkt heraus dagegen ist die
 * Verteilung das, was er sonst von Hand täte.
 *
 * Was keine passende Liste findet, wird **nicht** angelegt, sondern in
 * `ohneListe` gemeldet. Der Aufrufer fragt dann, ob die Liste entstehen soll
 * oder ob es doch in die Standardliste geht. Das ist die einzige Stelle, an
 * der die Verteilung eine Rückfrage auslöst.
 */
export async function addItemsSortiert(listId: string, raw: string): Promise<SortiertesErgebnis> {
  const listen = await allLists();
  const aktuelle = listen.find((liste) => liste.id === listId) ?? null;
  const verteilen = aktuelle?.is_default === true;
  const laeden = await allStores();

  const ergebnis: SortiertesErgebnis = { angelegt: [], umgeleitet: [], ohneListe: [] };

  for (const teil of splitItemInput(raw)) {
    const geparst = parseItemInput(teil);
    const shop = verteilen ? guessShop({ name: geparst.name, category: guessCategory(geparst.name) }) : null;

    if (shop === null) {
      const item = await addItem(listId, teil);
      if (item) ergebnis.angelegt.push(item);
      continue;
    }

    const ziel = listeFuerLaden(shop, listen, laeden);
    if (!ziel) {
      // Gefragt wird nur, wo der Supermarkt die Ware wirklich nicht führt.
      // Zahnpasta ohne Drogerie-Liste bleibt still liegen, wo sie getippt
      // wurde; eine Frage, die man mit "ist doch egal" beantwortet, hätte
      // man nicht stellen sollen.
      if (!fragtNachListe(shop)) {
        const item = await addItem(listId, teil);
        if (item) ergebnis.angelegt.push(item);
        continue;
      }
      ergebnis.ohneListe.push({ name: geparst.name, shop, raw: teil });
      continue;
    }

    const item = await addItem(ziel.id, teil);
    if (item) {
      ergebnis.angelegt.push(item);
      if (ziel.id !== listId) ergebnis.umgeleitet.push({ name: item.name, liste: ziel.name });
    }
  }

  return ergebnis;
}

/**
 * Hält den Familienkatalog nach: bekanntes Produkt zählt hoch, unbekanntes
 * kommt neu dazu. So lernt die Autovervollständigung mit jedem Einkauf.
 */
async function rememberProduct(name: string, normalized: string, unit: Unit | null): Promise<Product> {
  const timestamp = nowIso();
  const products = await db.products.where("family_id").equals(activeFamilyId()).toArray();
  const known = matchProduct(name, products).product;

  if (known) {
    const updated: Product = {
      ...known,
      use_count: known.use_count + 1,
      default_unit: known.default_unit ?? unit,
      /*
        Ein Produkt ohne Warengruppe bekommt beim nächsten Mal eine.

        Bis zum 20.09.2026 blieb es für immer ohne: "Milch" lag seit dem
        19.09. ohne Gruppe im Katalog, weil das Grundwissen erst in der Nacht
        dazukam, und jeder neue Artikel erbte die Lücke. Gesetzt wird nur, was
        leer ist; was jemand von Hand gewählt hat, bleibt unangetastet.
      */
      category: known.category ?? guessCategory(name),
      updated_at: timestamp,
      client_updated_at: timestamp,
    };
    await writeWithOutbox("products", updated);
    return updated;
  }

  const created: Product = {
    id: newId(),
    family_id: activeFamilyId(),
    name,
    normalized_name: normalized,
    // Was die App über deutsche Einkaufszettel weiß. Trifft es nicht zu,
    // bleibt die Warengruppe leer und jemand setzt sie einmal von Hand,
    // danach merkt der Katalog sie sich.
    category: guessCategory(name),
    default_unit: unit,
    use_count: 1,
    is_favorite: false,
    last_price_cents: null,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("products", created);
  return created;
}

export async function setItemChecked(id: string, checked: boolean): Promise<Item | null> {
  const current = await db.items.get(id);
  if (!current) return null;

  const timestamp = nowIso();
  const geschrieben: Item = {
    ...current,
    is_checked: checked,
    checked_at: checked ? timestamp : null,
    checked_by: checked ? activeUserId() : null,
    updated_at: timestamp,
    client_updated_at: timestamp,
  };
  await writeWithOutbox("items", geschrieben);
  return geschrieben;
}

/**
 * Rückgängig nach dem Abhaken, aber nur, wenn die Zeile noch genau diesen
 * Haken trägt.
 *
 * Hat inzwischen jemand anders denselben Artikel abgehakt, liegt er in dessen
 * Wagen, und ein Fehltipp hier darf ihn nicht wieder auf die Liste holen.
 * Hat jemand ihn wieder geöffnet, gibt es nichts rückgängig zu machen. In
 * beiden Fällen tut der Knopf nichts und meldet das mit `false`.
 */
export async function undoCheck(id: string, checkedAt: string): Promise<boolean> {
  const current = await db.items.get(id);
  if (!current || !current.is_checked || current.checked_by !== activeUserId() || current.checked_at !== checkedAt) return false;
  await setItemChecked(id, false);
  return true;
}

/**
 * "Nicht da": der Artikel bleibt offen, rutscht aber ans Ende der Liste und
 * merkt sich, dass er gefehlt hat. Beim Abschließen wird das zurückgesetzt.
 */
export async function setItemUnavailable(id: string, unavailable: boolean): Promise<void> {
  const current = await db.items.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("items", {
    ...current,
    unavailable_at: unavailable ? timestamp : null,
    is_checked: false,
    checked_at: null,
    checked_by: null,
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

/** Löschen heißt Grabstein setzen (Regel 2), sonst kommt die Zeile über den Sync zurück. */
export async function deleteItem(id: string): Promise<void> {
  const current = await db.items.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("items", {
    ...current,
    deleted_at: timestamp,
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

export async function itemsOfList(listId: string): Promise<Item[]> {
  const rows = await db.items.where("list_id").equals(listId).toArray();
  return rows.filter((row) => row.deleted_at === null);
}

export async function pendingCount(): Promise<number> {
  return db.outbox.count();
}

/**
 * Einkauf beginnen, beim Betreten des Einkaufsmodus.
 *
 * Erst dadurch weiß die Familie, dass jemand im Laden steht: Der Auslöser aus
 * Migration 0011 hängt am Einfügen dieser Zeile. Wer zwischendurch kurz auf
 * die Liste zurückgeht, setzt denselben Einkauf fort, sonst stünde die
 * Meldung zweimal auf dem anderen Telefon.
 */
export async function startTrip(storeId: string | null): Promise<Trip> {
  const laufend = findReusableTrip({ trips: await tripsOfFamily(), storeId, now: nowIso() });
  if (laufend) return laufend;

  const timestamp = nowIso();
  const trip: Trip = {
    id: newId(),
    family_id: activeFamilyId(),
    store_id: storeId,
    started_at: timestamp,
    finished_at: null,
    total_cents: null,
    started_by: activeUserId(),
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("trips", trip);
  return trip;
}

/**
 * Einkauf abschließen: erledigte Artikel wandern als Positionen in den
 * Einkauf, wiederkehrende bleiben offen in der Liste. Die Regel steht in
 * packages/core, hier wird nur geschrieben, jede Zeile über die Outbox.
 *
 * Ohne `tripId` entsteht der Einkauf hier. Das ist der Weg aus der
 * Listenansicht, wo jemand einen Einkauf abhakt, ohne den Einkaufsmodus
 * betreten zu haben.
 */
export async function finishTrip(
  listId: string,
  options: {
    storeId?: string | null;
    tripId?: string;
    startedAt?: string;
    startedBy?: string | null;
    totalCents?: number | null;
    receipt?: Receipt | null;
  } = {},
): Promise<Trip> {
  const items = await itemsOfList(listId);
  const timestamp = nowIso();

  const plan = planFinishTrip({
    items,
    tripId: options.tripId ?? newId(),
    familyId: activeFamilyId(),
    storeId: options.storeId ?? null,
    startedAt: options.startedAt ?? timestamp,
    now: timestamp,
    actorId: activeUserId(),
    startedBy: options.startedBy,
    totalCents: options.totalCents ?? null,
    newId,
  });

  await writeWithOutbox("trips", plan.trip);
  for (const tripItem of plan.tripItems) await writeWithOutbox("trip_items", tripItem);
  for (const item of plan.itemUpdates) await writeWithOutbox("items", item);

  // Der Bon kommt nach den Positionen: Er trägt die Preise in Zeilen ein, die
  // es vorher noch nicht gab.
  if (options.receipt) await applyReceipt(plan.trip, options.receipt);

  // Was als Vorrat geführt wird, füllt sich von selbst auf. Nur Bekanntes,
  // ein Einkauf legt keinen neuen Vorrat an (siehe planning/pantry).
  for (const aenderung of restockAfterTrip(await allPantryItems(), plan.tripItems)) {
    await updatePantryItem(aenderung.id, { quantity: aenderung.quantity });
  }

  if (options.storeId) await learnStoreOrder(options.storeId, items);

  return plan.trip;
}

/**
 * Was ein gescannter Bon in der Datenbank hinterlässt.
 *
 * Drei Dinge, und zwar in dieser Reihenfolge: der Preis an der Position des
 * Einkaufs (damit die Historie stimmt), eine Preisbeobachtung je Produkt
 * (damit der Verlauf wächst) und der zuletzt gezahlte Preis am Produkt
 * (damit die Schätzung der offenen Artikel besser wird).
 *
 * Die Preisbeobachtung wird nie überschrieben, sondern immer angehängt. Der
 * Verlauf ist der Sinn der Sache, ein einzelner aktueller Wert stünde schon
 * am Produkt.
 */
export async function applyReceipt(trip: Trip, receipt: Receipt): Promise<number> {
  const [tripItems, produkte] = await Promise.all([itemsOfTrip(trip.id), allProducts()]);
  const plan = planReceipt({ receipt, tripItems, products: produkte });
  const timestamp = nowIso();
  const beobachtetAm = receipt.purchased_at ? `${receipt.purchased_at.slice(0, 10)}T12:00:00.000Z` : timestamp;

  let geschrieben = 0;

  for (const treffer of plan.matched) {
    if (treffer.priceCents === null) continue;

    if (treffer.tripItemId !== null) {
      const position = tripItems.find((eintrag) => eintrag.id === treffer.tripItemId);
      if (position) {
        await writeWithOutbox("trip_items", {
          ...position,
          price_cents: treffer.priceCents,
          updated_at: timestamp,
          client_updated_at: timestamp,
        });
      }
    }

    if (treffer.productId === null) continue;

    const eintrag: PriceEntry = {
      id: newId(),
      family_id: activeFamilyId(),
      product_id: treffer.productId,
      store_id: trip.store_id,
      trip_id: trip.id,
      receipt_id: null,
      price_cents: treffer.priceCents,
      observed_at: beobachtetAm,
      updated_at: timestamp,
      client_updated_at: timestamp,
      deleted_at: null,
    };
    await writeWithOutbox("price_entries", eintrag);
    await rememberPrice(treffer.productId, treffer.priceCents);
    geschrieben += 1;
  }

  return geschrieben;
}

/** Alle beobachteten Preise eines Produkts, für den Verlauf. */
export async function priceEntriesOfProduct(productId: string): Promise<PriceEntry[]> {
  const rows = await db.price_entries.where("product_id").equals(productId).toArray();
  return rows.filter((row) => row.deleted_at === null);
}

export async function productById(productId: string): Promise<Product | null> {
  const product = await db.products.get(productId);
  return product && product.deleted_at === null ? product : null;
}

/**
 * Nach dem Einkauf rückt die Gangfolge des Ladens in Richtung der tatsächlich
 * gelaufenen Reihenfolge. Wie schnell, hängt an der Konfidenz der Kette: ein
 * gut belegtes Kettenmuster wird vorsichtig angefasst, eine Startannahme
 * schnell überschrieben.
 */
async function learnStoreOrder(storeId: string, items: readonly Item[]): Promise<void> {
  const store = await db.stores.get(storeId);
  if (!store || store.deleted_at !== null) return;

  const observed = observedCategoryOrder(items);
  if (observed.length < 2) return;

  const confidence = store.chain ? chainProfile(store.chain).confidence : null;
  const learned = learnCategoryOrder(store.category_order, observed, learningRateFor(confidence));
  if (learned.join("|") === store.category_order.join("|")) return;

  const timestamp = nowIso();
  await writeWithOutbox("stores", { ...store, category_order: learned, updated_at: timestamp, client_updated_at: timestamp });
}

// --- Listen ---------------------------------------------------------------

export async function allLists(): Promise<List[]> {
  const rows = await db.lists.where("family_id").equals(activeFamilyId()).toArray();
  return rows
    .filter((row) => row.deleted_at === null)
    .sort((a, b) => a.sort_index - b.sort_index || a.name.localeCompare(b.name, "de-DE"));
}

export async function listById(id: string): Promise<List | null> {
  const row = await db.lists.get(id);
  return row && row.deleted_at === null ? row : null;
}

export async function createList(name: string, storeId: string | null = null): Promise<List> {
  const trimmed = name.trim();
  if (trimmed.length === 0) throw new Error("Die Liste braucht einen Namen");

  const existing = await allLists();
  const timestamp = nowIso();
  const list: List = {
    id: newId(),
    family_id: activeFamilyId(),
    name: trimmed,
    store_id: storeId,
    is_default: existing.length === 0,
    sort_index: existing.length,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("lists", list);
  return list;
}

export async function updateList(id: string, patch: Partial<Pick<List, "name" | "store_id">>): Promise<void> {
  const current = await db.lists.get(id);
  if (!current) return;
  const timestamp = nowIso();
  await writeWithOutbox("lists", { ...current, ...patch, updated_at: timestamp, client_updated_at: timestamp });
}

/** Löschen heißt Grabstein, auch für die Artikel darin. */
export async function deleteList(id: string): Promise<void> {
  const current = await db.lists.get(id);
  if (!current) return;

  const timestamp = nowIso();
  const items = await itemsOfList(id);
  for (const item of items) {
    await writeWithOutbox("items", { ...item, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
  }
  await writeWithOutbox("lists", { ...current, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
}

// --- Artikel --------------------------------------------------------------

export interface ItemPatch {
  name?: string;
  quantity?: number | null;
  unit?: Unit | null;
  note?: string | null;
  category?: CategoryKey | null;
  is_recurring?: boolean;
  only_on_sale?: boolean;
}

export async function updateItem(id: string, patch: ItemPatch): Promise<void> {
  const current = await db.items.get(id);
  if (!current) return;

  const timestamp = nowIso();
  const name = patch.name?.trim();
  await writeWithOutbox("items", {
    ...current,
    ...patch,
    ...(name && name.length > 0 ? { name, normalized_name: normalizeName(name) } : {}),
    updated_at: timestamp,
    client_updated_at: timestamp,
  });

  // Was am Artikel korrigiert wurde, lernt der Katalog mit: beim nächsten Mal
  // stimmen Kategorie und Einheit dann von allein.
  if (current.product_id !== null && (patch.category !== undefined || patch.unit !== undefined)) {
    const product = await db.products.get(current.product_id);
    if (product && product.deleted_at === null) {
      await writeWithOutbox("products", {
        ...product,
        category: patch.category ?? product.category,
        default_unit: patch.unit ?? product.default_unit,
        updated_at: timestamp,
        client_updated_at: timestamp,
      });
    }
  }
}

// --- Läden ----------------------------------------------------------------

export async function allStores(): Promise<Store[]> {
  const rows = await db.stores.where("family_id").equals(activeFamilyId()).toArray();
  return rows.filter((row) => row.deleted_at === null).sort((a, b) => a.name.localeCompare(b.name, "de-DE"));
}

/**
 * Beim Anlegen wird die Gangfolge der Kette kopiert. Ab dann gehört sie der
 * Filiale und lernt dort nach, statt die Kette für alle zu verändern.
 */
export async function createStore(name: string, chain: ChainKey | null): Promise<Store> {
  const trimmed = name.trim();
  if (trimmed.length === 0) throw new Error("Der Laden braucht einen Namen");

  const existing = await allStores();
  const timestamp = nowIso();
  const store: Store = {
    id: newId(),
    family_id: activeFamilyId(),
    name: trimmed,
    chain,
    category_order: chain ? [...chainProfile(chain).categoryOrder] : [],
    is_default: existing.length === 0,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("stores", store);
  return store;
}

/**
 * Der Laden, in dem gerade eingekauft wird.
 *
 * Im Laden zählt, wo man steht, nicht was mal in den Einstellungen eingetragen
 * wurde: Die Familie kauft bei ALDI, Rewe und Lidl, und die Gangfolgen sind
 * verschieden. Gewählt wird eine Kette, kein Laden mit Namen; den Eintrag im
 * Katalog legt diese Funktion bei Bedarf selbst an, damit niemand im
 * Supermarkt ein Formular ausfüllt.
 *
 * Gesetzt wird beides: die Liste (danach sortiert der Einkaufsmodus) und der
 * laufende Einkauf (der landet so mit dem richtigen Laden in der Historie).
 */
export async function waehleLaden(
  listId: string,
  tripId: string | null,
  wahl: { storeId: string } | { chain: ChainKey } | { keiner: true },
): Promise<string | null> {
  let storeId: string | null = null;

  if ("storeId" in wahl) {
    storeId = wahl.storeId;
  } else if ("chain" in wahl) {
    const vorhanden = (await allStores()).find((laden) => laden.chain === wahl.chain);
    storeId = vorhanden ? vorhanden.id : (await createStore(chainProfile(wahl.chain).name, wahl.chain)).id;
  }

  await updateList(listId, { store_id: storeId });

  if (tripId !== null) {
    const trip = await db.trips.get(tripId);
    if (trip && trip.deleted_at === null && trip.finished_at === null) {
      const timestamp = nowIso();
      await writeWithOutbox("trips", { ...trip, store_id: storeId, updated_at: timestamp, client_updated_at: timestamp });
    }
  }

  return storeId;
}

export async function deleteStore(id: string): Promise<void> {
  const current = await db.stores.get(id);
  if (!current) return;
  const timestamp = nowIso();
  await writeWithOutbox("stores", { ...current, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
}

// --- Mitglieder -----------------------------------------------------------

export async function allMembers(): Promise<Member[]> {
  const rows = await db.members.where("family_id").equals(activeFamilyId()).toArray();
  return rows.filter((row) => row.deleted_at === null).sort((a, b) => a.display_name.localeCompare(b.display_name, "de-DE"));
}

// --- Familienkatalog ---------------------------------------------------------

export async function allProducts(): Promise<Product[]> {
  const rows = await db.products.where("family_id").equals(activeFamilyId()).toArray();
  return rows.filter((row) => row.deleted_at === null);
}

/** Vorschläge für die Schnelleingabe. Läuft lokal gegen Dexie, ohne Netz. */
export async function suggestions(query: string, limit = 6): Promise<Product[]> {
  return suggestProducts(query, await allProducts(), limit);
}

export async function favorites(limit = 12): Promise<Product[]> {
  const products = await allProducts();
  return products
    .filter((product) => product.is_favorite)
    .sort((a, b) => b.use_count - a.use_count || a.name.localeCompare(b.name, "de-DE"))
    .slice(0, limit);
}

export async function toggleFavorite(productId: string): Promise<void> {
  const product = await db.products.get(productId);
  if (!product) return;
  const timestamp = nowIso();
  await writeWithOutbox("products", {
    ...product,
    is_favorite: !product.is_favorite,
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

/** Gangfolge für eine Liste: die des Ladens, sonst ein neutrales Profil. */
export async function categoryOrderForList(list: List | null): Promise<CategoryKey[]> {
  if (list?.store_id) {
    const store = await db.stores.get(list.store_id);
    if (store && store.deleted_at === null && store.category_order.length > 0) {
      return store.category_order;
    }
  }
  return [...chainProfile("rewe").categoryOrder];
}

// --- Übernahme nach der Anmeldung --------------------------------------------

/**
 * Ersetzt in created_by und checked_by jede Kennung, die der Server nicht
 * auflösen kann, durch das angemeldete Konto, in den Zeilen und in allem, was
 * noch in der Outbox liegt.
 *
 * Nötig für Geräte, auf denen schon Artikel mit einer falschen Kennung
 * entstanden sind. Sie scheitern sonst bis in alle Ewigkeit am
 * Fremdschlüssel, ohne dass jemand sieht, warum.
 */
async function repariereAkteure(familyId: string, userId: string): Promise<number> {
  const timestamp = nowIso();

  // Was der Server auflösen kann: die Konten der Mitglieder dieser Familie.
  // Alles andere in einem Personenfeld lässt die Zeile am Fremdschlüssel
  // scheitern, egal woher der Wert stammt.
  const mitglieder = await db.members.where("family_id").equals(familyId).toArray();

  /*
    Ohne geladene Mitglieder wird nicht repariert.

    Sonst gälte beim ersten Start nach der Anmeldung jedes fremde Konto als
    unbekannt, und die Artikel der anderen bekämen stillschweigend den eigenen
    Namen und würden so hochgeladen. Lieber eine Runde später reparieren als
    fremde Urheberschaft überschreiben.
  */
  if (mitglieder.length === 0) return 0;

  const erlaubt = new Set<string>([userId, ...mitglieder.map((mitglied) => mitglied.user_id)]);

  const geplant = planActorRepair({
    rows: await db.items.toArray(),
    erlaubt,
    toActor: userId,
    now: timestamp,
  });

  for (const item of geplant) await writeWithOutbox("items", item);
  const repariert = geplant.length;

  // Einträge, die noch mit der alten Kennung in der Outbox warten, würden die
  // gerade reparierte Zeile sonst wieder überschreiben.
  const falsch = (wert: string | null | undefined) => typeof wert === "string" && wert.length > 0 && !erlaubt.has(wert);
  const wartend = await db.outbox.where("table").equals("items").toArray();
  for (const eintrag of wartend) {
    const nutzlast = eintrag.payload as { created_by?: string | null; checked_by?: string | null };
    if (!falsch(nutzlast.created_by) && !falsch(nutzlast.checked_by)) continue;

    // Die Nutzlast ist in der Outbox generisch über alle Tabellen typisiert.
    // Hier ist sicher, dass es eine Artikelzeile ist, die Abfrage filtert
    // darauf.
    const reparierterEintrag = {
      ...eintrag,
      payload: {
        ...(eintrag.payload as Item),
        created_by: falsch(nutzlast.created_by) ? userId : (nutzlast.created_by ?? null),
        checked_by: falsch(nutzlast.checked_by) ? userId : (nutzlast.checked_by ?? null),
      },
      attempts: 0,
      next_attempt_at: timestamp,
      last_error: null,
    };
    await db.outbox.put(reparierterEintrag);
  }

  return repariert;
}

/**
 * Weckt alles auf, was die Engine nach zwölf Fehlversuchen für ein Jahr aus
 * dem Verkehr gezogen hat.
 *
 * Wird beim Start gerufen: Nach einer neuen Fassung der App ist der Grund für
 * das Scheitern womöglich behoben, und niemand soll dafür die Datenbank des
 * Browsers leeren müssen.
 */
export async function weckeOutbox(): Promise<number> {
  const timestamp = nowIso();
  const wartend = await db.outbox.toArray();
  const geparkt = wartend.filter((eintrag) => Date.parse(eintrag.next_attempt_at) > Date.now());

  for (const eintrag of geparkt) {
    await db.outbox.update(eintrag.id, { next_attempt_at: timestamp, attempts: 0 });
  }

  return geparkt.length;
}

/**
 * Tabellen, deren Zeilen auf diesem Gerät entstehen und eine family_id tragen.
 * `members` und `families` fehlen mit Absicht: Sie kommen nur vom Server.
 */
const FAMILIEN_TABELLEN = [
  "stores",
  "lists",
  "items",
  "products",
  "trips",
  "trip_items",
  "price_entries",
  "recipes",
  "recipe_ingredients",
  "pantry_items",
  "meal_plan_entries",
  "invites",
] as const;

/**
 * Holt Zeilen mit einer fremden family_id in die aktive Familie.
 *
 * Am 20.09.2026 hing auf Daniels iPhone seit Stunden eine Liste "Einkauf" mit
 * "new row violates row-level security policy for table lists". Die Zeile trug
 * die Kennung einer lokalen Familie, die es auf dem Server nie gab: Die
 * Übernahme beim Anmelden läuft nur einmal je Familie (markAdoptionDone) und
 * nur für genau die eine lokale Familie, die gerade im Speicher des Browsers
 * steht. Wer sich abmeldet und vor der nächsten Anmeldung etwas tippt, und
 * jedes iPhone, dem Safari nach sieben Tagen den Speicher der nicht
 * installierten Seite leert, erzeugt eine zweite lokale Familie. Deren Zeilen
 * sah niemand mehr an.
 *
 * Läuft deshalb bei jedem Start, nicht einmal je Familie.
 *
 * Eine leere Liste, die es in der Familie schon unter demselben Namen gibt,
 * wird verworfen statt hochgeladen: Sonst stünde nach der Reparatur ein
 * zweites "Einkauf" auf dem Telefon aller.
 */
async function repariereFamilien(familyId: string): Promise<number> {
  const mitglieder = await db.members.toArray();

  /*
    Ohne geladene Mitglieder wird nicht repariert. Sonst gälte beim ersten
    Start nach der Anmeldung jede zweite Familie des Geräts als unbekannt, und
    ihre Zeilen wanderten in die gerade aktive.
  */
  if (mitglieder.length === 0) return 0;

  const familien = await db.families.toArray();
  const erlaubt = new Set<string>([familyId, ...familien.map((familie) => familie.id), ...mitglieder.map((mitglied) => mitglied.family_id)]);

  const timestamp = nowIso();
  const hatStandardliste = (await db.lists.where("family_id").equals(familyId).toArray()).some(
    (liste) => liste.deleted_at === null && liste.is_default,
  );
  const bekannteListen = new Set(
    (await db.lists.where("family_id").equals(familyId).toArray())
      .filter((liste) => liste.deleted_at === null)
      .map((liste) => liste.name.trim().toLowerCase()),
  );

  let repariert = 0;
  for (const tabelle of FAMILIEN_TABELLEN) {
    const geplant = planFamilyRepair({
      rows: await db.table<FamilyScopedRow>(tabelle).toArray(),
      erlaubt,
      toFamily: familyId,
      now: timestamp,
    });

    for (const row of geplant) {
      if (tabelle === "lists") {
        const artikel = await db.items.where("list_id").equals(row.id).toArray();
        const leer = artikel.every((eintrag) => eintrag.deleted_at !== null);
        if (leer && bekannteListen.has((row as unknown as List).name.trim().toLowerCase())) {
          await db.outbox.where("row_id").equals(row.id).delete();
          await db.lists.delete(row.id);
          repariert += 1;
          continue;
        }
        /*
          Eine mitgebrachte Liste wird nie zur zweiten Standardliste.

          Genau das ist am 20.09.2026 passiert: Eine lokal entstandene Liste
          "Einkauf" trug is_default und kam so neben das vorhandene
          "Supermarkt" zu stehen. Danach entschied der Zufall, welche galt.
        */
        if ((row as unknown as List).is_default && hatStandardliste) {
          (row as unknown as List).is_default = false;
        }
      }

      // Die alten Einträge zeigen auf die fremde Familie und würden weiter
      // abgewiesen. Sie werden durch den reparierten Stand ersetzt.
      await db.outbox.where("row_id").equals(row.id).delete();
      await writeWithOutbox(tabelle, row);
      repariert += 1;
    }
  }

  // Wartende Einträge ohne Zeile: Die Zeile ist weg, der Fehler bliebe stehen.
  for (const eintrag of await db.outbox.toArray()) {
    const familie = (eintrag.payload as { family_id?: unknown }).family_id;
    if (typeof familie !== "string" || erlaubt.has(familie)) continue;
    await db.outbox.delete(eintrag.id);
    repariert += 1;
  }

  return repariert;
}

/**
 * Gibt Produkten und Artikeln ohne Warengruppe eine, wenn das Grundwissen
 * inzwischen eine kennt.
 *
 * Am 20.09.2026 lagen auf der Liste der Familie "Milch", "Äpfel", "Schinken"
 * und "TK Pizze" ohne Gruppe und damit im Laden am falschen Ende: Sie waren
 * am Abend davor entstanden, Stunden bevor guessCategory dazukam. Ohne diese
 * Reparatur bliebe das so, denn ein bekanntes Produkt wurde nie neu bewertet.
 *
 * Gesetzt wird ausschließlich, was leer ist. Eine Gruppe, die jemand von Hand
 * gewählt hat, ist eine Entscheidung und kein Platzhalter.
 */
export async function repariereWarengruppen(familyId: string): Promise<number> {
  const timestamp = nowIso();
  let repariert = 0;

  const produkte = await db.products.where("family_id").equals(familyId).toArray();
  const gruppeJeProdukt = new Map<string, CategoryKey>();
  for (const produkt of produkte) {
    if (produkt.deleted_at !== null) continue;
    const gruppe = produkt.category ?? guessCategory(produkt.name);
    if (gruppe === null) continue;
    gruppeJeProdukt.set(produkt.id, gruppe);
    if (produkt.category !== null) continue;
    await writeWithOutbox("products", { ...produkt, category: gruppe, updated_at: timestamp, client_updated_at: timestamp });
    repariert += 1;
  }

  const artikel = await db.items.where("family_id").equals(familyId).toArray();
  for (const zeile of artikel) {
    if (zeile.deleted_at !== null || zeile.category !== null) continue;
    const gruppe = (zeile.product_id !== null ? gruppeJeProdukt.get(zeile.product_id) : undefined) ?? guessCategory(zeile.name);
    if (!gruppe) continue;
    await writeWithOutbox("items", { ...zeile, category: gruppe, updated_at: timestamp, client_updated_at: timestamp });
    repariert += 1;
  }

  return repariert;
}

/**
 * Markiert Salz, Pfeffer und Öl in den Rezepten, die schon da sind.
 *
 * Das Grundwissen über Grundzutaten kam am 20.09.2026 dazu. Es wirkt beim
 * Anlegen, beim Einlesen und beim Spiegeln, also nur für Zutaten, die danach
 * entstehen. Die 44 Rezepte der Familie lagen zu dem Zeitpunkt längst in
 * Dexie, und ohne diesen Nachzug käme Salz dort weiter auf den Zettel.
 *
 * **Genau einmal je Gerät**, danach nie wieder. Der Schalter "Immer im Haus"
 * ist eine Entscheidung, und eine Reparatur, die jede Runde läuft, würde sie
 * jedes Mal überschreiben. Dass zwei Telefone die Runde je einmal fahren,
 * ist hingenommen: Sie setzen dasselbe Ergebnis, und das Zeitfenster
 * dazwischen ist die erste Sync-Runde nach dem Update.
 *
 * Gesetzt wird nur, was noch nicht gesetzt ist. Weggenommen wird nie etwas:
 * Wer den Schalter von Hand umlegt, hat recht, auch gegen die Tabelle.
 */
export async function repariereGrundzutaten(familyId: string): Promise<number> {
  const marke = `ainkauf.grundzutaten.${familyId}`;
  if (globalThis.localStorage?.getItem(marke) !== null) return 0;

  const timestamp = nowIso();
  let repariert = 0;

  for (const zutat of await db.recipe_ingredients.where("family_id").equals(familyId).toArray()) {
    if (zutat.deleted_at !== null || zutat.is_staple) continue;
    if (!guessStaple({ name: zutat.name, quantity: zutat.quantity, unit: zutat.unit })) continue;
    await writeWithOutbox("recipe_ingredients", { ...zutat, is_staple: true, updated_at: timestamp, client_updated_at: timestamp });
    repariert += 1;
  }

  globalThis.localStorage?.setItem(marke, timestamp);
  return repariert;
}

/**
 * Holt alles, was vor der Anmeldung lokal entstanden ist, in die echte
 * Familie: die Einkaufsliste, die jemand im Zug getippt hat, soll nach dem
 * Anmelden nicht verschwinden.
 *
 * Ohne diesen Schritt trügen die Zeilen eine family_id, die es auf dem Server
 * nicht gibt. Der Sync würde sie bis zum Aufgeben abweisen, ohne dass jemand
 * es merkt.
 */
export async function adoptLocalData(familyId: string, userId: string): Promise<number> {
  const lokal = localFamilyId();

  /*
    Der lokale Akteur ist die Gerätekennung, nicht das, was activeUserId
    gerade liefert.

    Vorher stand setActiveIdentity zuerst und fromActor las danach schon das
    neue Konto. planAdoption verglich also das neue Konto mit sich selbst, und
    jedes created_by behielt die lokale Gerätekennung. Diese Kennung gibt es in
    auth.users nicht, der Fremdschlüssel auf items.created_by schlug zu, und
    kein einziger Artikel kam je auf dem Server an. Listen und Produkte haben
    kein created_by, die gingen durch: Deshalb sah es so aus, als würde der
    Abgleich laufen.
  */
  const lokalerAkteur = localMemberId();

  /*
    Die Lesemarken des Abgleichs gelten je Tabelle, nicht je Familie. Wechselt
    die aktive Familie, zeigen sie auf einen Zeitpunkt, der für die neue
    Familie nichts bedeutet: Alles, was älter ist als die Marke, wird nie
    geholt. Genau so kann eine Liste, die es auf dem Server gibt, auf einem
    Gerät für immer fehlen. Beim Wechsel werden sie deshalb geleert.
  */
  const vorher = activeFamilyId();
  setActiveIdentity(familyId, userId);
  if (vorher !== familyId) await db.cursors.clear();

  // Altlasten zuerst: Zeilen, die schon in der echten Familie liegen, aber
  // noch die Gerätekennung tragen. Sie entstehen bei jeder Anlage zwischen
  // Anmeldung und Übernahme.
  await repariereAkteure(familyId, userId);

  // Zeilen aus einer lokalen Familie, die die Übernahme nie gesehen hat.
  await repariereFamilien(familyId);

  if (lokal === familyId || adoptionDone(familyId)) return 0;

  const timestamp = nowIso();
  const tabellen = ["lists", "items", "products", "stores"] as const;
  let uebernommen = 0;

  for (const tabelle of tabellen) {
    const rows = await db.table<FamilyScopedRow>(tabelle).where("family_id").equals(lokal).toArray();
    const geplant = planAdoption({
      rows,
      fromFamily: lokal,
      toFamily: familyId,
      fromActor: lokalerAkteur,
      toActor: userId,
      now: timestamp,
    });

    for (const row of geplant) {
      // Eine übernommene Liste wird nie zur zweiten Standardliste.
      if (tabelle === "lists" && (row as unknown as List).is_default) {
        const schonEine = (await db.lists.where("family_id").equals(familyId).toArray()).some(
          (liste) => liste.deleted_at === null && liste.is_default && liste.id !== row.id,
        );
        if (schonEine) (row as unknown as List).is_default = false;
      }

      // Die alten Einträge in der Outbox zeigen auf die lokale Familie und
      // würden abgewiesen. Sie werden durch den übernommenen Stand ersetzt.
      await db.outbox.where("row_id").equals(row.id).delete();
      await writeWithOutbox(tabelle, row);
      uebernommen += 1;
    }
  }

  markAdoptionDone(familyId);
  return uebernommen;
}

// --- Einladungen -------------------------------------------------------------

/** Gültige Einladung der Familie, sonst null. */
export async function activeInvite(): Promise<Invite | null> {
  const now = Date.now();
  const rows = await db.invites.where("family_id").equals(activeFamilyId()).toArray();
  return (
    rows
      .filter((row) => row.deleted_at === null && Date.parse(row.expires_at) > now && row.used_count < row.max_uses)
      .sort((a, b) => Date.parse(b.expires_at) - Date.parse(a.expires_at))[0] ?? null
  );
}

/**
 * Neue Einladung. Der Code entsteht auf dem Gerät, damit er auch offline
 * sofort sichtbar ist. Zeichen, die man verwechseln kann (O, 0, I, 1), sind
 * nicht dabei, weil der Code am Telefon vorgelesen und abgetippt wird.
 */
export async function createInvite(days = 30): Promise<Invite> {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const code = Array.from({ length: 6 }, () => alphabet[Math.floor(Math.random() * alphabet.length)] ?? "A").join("");

  const timestamp = nowIso();
  const invite: Invite = {
    id: newId(),
    family_id: activeFamilyId(),
    code,
    created_by: activeUserId(),
    expires_at: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
    max_uses: 20,
    used_count: 0,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("invites", invite);
  return invite;
}

// --- Einkäufe und Auswertung -------------------------------------------------

/** Alle Einkäufe der Familie, auch die noch laufenden. Neueste zuerst. */
async function tripsOfFamily(): Promise<Trip[]> {
  const rows = await db.trips.where("family_id").equals(activeFamilyId()).toArray();
  return rows
    .filter((row) => row.deleted_at === null)
    .sort((a, b) => Date.parse(b.finished_at ?? b.started_at) - Date.parse(a.finished_at ?? a.started_at));
}

/**
 * Abgeschlossene Einkäufe, für Historie und Ausgaben.
 *
 * Seit der Einkauf beim Betreten des Einkaufsmodus entsteht, gibt es auch
 * laufende. Ein Einkauf, der noch im Wagen liegt, gehört in keine Auswertung
 * und in keine Liste vergangener Einkäufe.
 */
export async function allTrips(): Promise<Trip[]> {
  return (await tripsOfFamily()).filter((row) => row.finished_at !== null);
}

export async function tripById(id: string): Promise<Trip | null> {
  const trip = await db.trips.get(id);
  return trip && trip.deleted_at === null ? trip : null;
}

/**
 * Der offene Einkauf, den gerade jemand anders führt, sonst null.
 *
 * Dieselbe Frist wie beim Fortsetzen (findReusableTrip): Ein vergessener
 * Einkauf von gestern soll nicht bis in alle Ewigkeit "kauft gerade ein"
 * melden. Steht in Dexie, kommt seit Migration 0014 über Realtime.
 */
export async function shoppingRightNow(): Promise<Trip | null> {
  const ich = activeUserId();
  const grenze = Date.now() - TRIP_REUSE_HOURS * 3600_000;
  return (
    (await tripsOfFamily()).find(
      (trip) => trip.finished_at === null && trip.started_by !== null && trip.started_by !== ich && Date.parse(trip.started_at) >= grenze,
    ) ?? null
  );
}

export async function itemsOfTrip(tripId: string): Promise<TripItem[]> {
  const rows = await db.trip_items.where("trip_id").equals(tripId).toArray();
  return rows.filter((row) => row.deleted_at === null).sort((a, b) => a.name.localeCompare(b.name, "de-DE"));
}

export async function currentFamily(): Promise<Family | null> {
  const family = await db.families.get(activeFamilyId());
  return family && family.deleted_at === null ? family : null;
}

/**
 * Ohne Anmeldung gibt es keine Familienzeile, nur eine lokale Kennung. Für
 * Einstellungen wie das Monatsbudget braucht es aber eine Zeile. Sie entsteht
 * hier und bleibt lokal: angemeldet kommt die echte Familie über den Sync.
 *
 * Schreibt, gehört also nicht in eine Live-Query.
 */
export async function ensureLocalFamily(): Promise<Family | null> {
  const id = activeFamilyId();
  if (id !== localFamilyId()) return currentFamily();

  const vorhanden = await db.families.get(id);
  if (vorhanden) return vorhanden;

  const timestamp = nowIso();
  const family: Family = {
    id,
    name: "Diese Familie",
    monthly_budget_cents: null,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };
  await db.families.put(family);
  return family;
}

/**
 * Monatsbudget setzen. Geht als Änderung in die Outbox, nicht als Upsert:
 * in families darf niemand einfügen, Familien entstehen nur über
 * create_family().
 */
export async function setMonthlyBudget(cents: number | null): Promise<void> {
  // Falls die lokale Familienzeile noch nicht steht, hier anlegen. Sonst
  // hinge das Speichern davon ab, ob ein Effekt schon gelaufen ist.
  const family = (await currentFamily()) ?? (await ensureLocalFamily());
  if (!family) return;

  const timestamp = nowIso();
  const geaendert: Family = { ...family, monthly_budget_cents: cents, updated_at: timestamp, client_updated_at: timestamp };

  // Die lokale Familie gibt es auf dem Server nicht, ihre Änderung bleibt hier.
  if (family.id === localFamilyId()) {
    await db.families.put(geaendert);
    return;
  }

  await writeWithOutbox("families", geaendert, "update");
}

/** Positionen eines alten Einkaufs zurück auf eine Liste legen. */
export async function repeatTrip(tripId: string, listId: string): Promise<number> {
  const positions = await itemsOfTrip(tripId);
  for (const position of positions) {
    await addItem(listId, position.quantity && position.unit ? `${position.quantity} ${position.unit} ${position.name}` : position.name);
  }
  return positions.length;
}

/** Preis eines Produkts festhalten, damit die Schätzung besser wird. */
export async function rememberPrice(productId: string, priceCents: number): Promise<void> {
  const product = await db.products.get(productId);
  if (!product) return;
  const timestamp = nowIso();
  await writeWithOutbox("products", {
    ...product,
    last_price_cents: priceCents,
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

// --- Artikelfotos ------------------------------------------------------------

/**
 * Nimmt ein Bild an: verkleinern, lokal ablegen, in die Warteschlange. Der
 * Upload passiert später im Sync, damit ein Foto im Laden nicht am Empfang
 * scheitert.
 */
export async function queuePhoto(itemId: string, file: File | Blob): Promise<void> {
  const blob = await verkleinern(file);
  await db.photo_queue.put({
    id: newId(),
    item_id: itemId,
    family_id: activeFamilyId(),
    // Bytes statt Blob: siehe Kommentar an PhotoQueueEntry.
    bytes: await blob.arrayBuffer(),
    mime: blob.type || "image/jpeg",
    created_at: nowIso(),
    attempts: 0,
    last_error: null,
  });
  outboxGeaendert();
}

export async function pendingPhoto(itemId: string): Promise<PhotoQueueEntry | null> {
  return (await db.photo_queue.where("item_id").equals(itemId).first()) ?? null;
}

export async function duePhotos(limit = 3): Promise<PhotoQueueEntry[]> {
  return db.photo_queue.orderBy("created_at").limit(limit).toArray();
}

export async function photoUploaded(queueId: string, itemId: string, path: string): Promise<void> {
  const item = await db.items.get(itemId);
  if (item) {
    const timestamp = nowIso();
    await writeWithOutbox("items", { ...item, photo_path: path, updated_at: timestamp, client_updated_at: timestamp });
  }
  await db.photo_queue.delete(queueId);
}

export async function photoFailed(queueId: string, error: string): Promise<void> {
  const entry = await db.photo_queue.get(queueId);
  if (!entry) return;
  await db.photo_queue.put({ ...entry, attempts: entry.attempts + 1, last_error: error });
}

export async function removePhoto(itemId: string): Promise<void> {
  const item = await db.items.get(itemId);
  await db.photo_queue.where("item_id").equals(itemId).delete();
  if (!item) return;
  const timestamp = nowIso();
  await writeWithOutbox("items", { ...item, photo_path: null, updated_at: timestamp, client_updated_at: timestamp });
}

/** Was noch auf Übertragung wartet, für die Anzeige in den Einstellungen. */
/**
 * Was dieses Gerät über sich selbst weiß.
 *
 * Gebaut am 20.09.2026, weil zwei Geräte derselben Familie unterschiedliche
 * Listen zeigten und von außen nicht zu erkennen war, woran es liegt. Der
 * Server war nachweislich für beide Konten gleich. Diese Auskunft macht
 * sichtbar, was das Gerät für seine Familie hält, was es geladen hat und wie
 * weit seine Lesemarken stehen.
 */
export interface Selbstauskunft {
  aktiveFamilie: string;
  lokaleFamilie: string;
  uebernommen: boolean;
  zeilen: { listen: number; artikel: number; produkte: number; mitglieder: number };
  marken: { tabelle: string; stand: string }[];
}

export async function selbstauskunft(): Promise<Selbstauskunft> {
  const aktiv = activeFamilyId();
  const [listen, artikel, produkte, mitglieder, marken] = await Promise.all([
    db.lists.where("family_id").equals(aktiv).count(),
    db.items.where("family_id").equals(aktiv).count(),
    db.products.where("family_id").equals(aktiv).count(),
    db.members.where("family_id").equals(aktiv).count(),
    db.cursors.toArray(),
  ]);

  return {
    aktiveFamilie: aktiv,
    lokaleFamilie: localFamilyId(),
    uebernommen: adoptionDone(aktiv),
    zeilen: { listen, artikel, produkte, mitglieder },
    marken: marken
      .filter((marke) => marke.updated_at !== null)
      .map((marke) => ({ tabelle: marke.table, stand: (marke.updated_at ?? "").slice(0, 16).replace("T", " ") })),
  };
}

/**
 * Setzt die Lesemarken zurück und holt damit beim nächsten Lauf alles neu.
 *
 * Der Notausgang, wenn ein Gerät aus irgendeinem Grund an einer Marke
 * vorbeigelaufen ist und Zeilen nie gesehen hat. Kostet einen vollen Abgleich
 * und ist deshalb ein Knopf und kein Automatismus.
 */
export async function alleNeuLaden(): Promise<void> {
  await db.cursors.clear();
}

export interface HaengendeAenderung {
  table: string;
  name: string;
  error: string;
  attempts: number;
}

/**
 * Was der Abgleich gerade macht, und woran er hängt.
 *
 * Die Liste der hängenden Zeilen steht hier, weil sie in den Einstellungen
 * sichtbar sein muss. Am 19.09.2026 blieben 39 Artikel unübertragen, und die
 * einzige Spur war eine Zahl im Abzeichen. Wer den Grund nicht sieht, kann
 * ihn nicht melden, und ich kann ihn nicht finden.
 */
export async function syncStatus(): Promise<{
  pending: number;
  photos: number;
  lastError: string | null;
  stuck: HaengendeAenderung[];
}> {
  const eintraege = await db.outbox.toArray();
  const mitFehler = eintraege.filter((entry) => entry.last_error !== null);

  return {
    pending: eintraege.length,
    photos: await db.photo_queue.count(),
    lastError: mitFehler.at(-1)?.last_error ?? null,
    stuck: mitFehler
      .sort((a, b) => b.attempts - a.attempts)
      .slice(0, 5)
      .map((entry) => ({
        table: entry.table,
        name: String((entry.payload as { name?: unknown }).name ?? entry.row_id.slice(0, 8)),
        error: entry.last_error ?? "",
        attempts: entry.attempts,
      })),
  };
}

// --- Sprachassistent ---------------------------------------------------------

/**
 * Übernimmt, was der Assistent verstanden hat. Alles läuft über dieselben
 * Wege wie eine Eingabe von Hand: eine Zeile, ein Outbox-Eintrag. Der
 * Assistent bekommt keine Abkürzung am Sync vorbei.
 */
export async function applyAssistantResult(result: ResolvedResult): Promise<number> {
  let angelegt = 0;

  for (const action of result.actions) {
    if (action.type === "create_list") {
      const neu = await createList(action.listName);
      for (const item of action.items) {
        await addItem(neu.id, item.name);
        angelegt += 1;
      }
      continue;
    }

    const listId = action.listId;
    if (listId === null) continue;

    for (const item of action.items) {
      if (action.type === "add") {
        const angelegterArtikel = await addItem(listId, item.name);
        if (angelegterArtikel && (item.quantity !== null || item.unit !== null || item.category !== null)) {
          await updateItem(angelegterArtikel.id, {
            quantity: item.quantity ?? angelegterArtikel.quantity,
            unit: item.unit ?? angelegterArtikel.unit,
            category: item.category ?? angelegterArtikel.category,
          });
        }
        angelegt += 1;
        continue;
      }

      // Abhaken und Entfernen brauchen den vorhandenen Artikel.
      const vorhanden = (await itemsOfList(listId)).find(
        (zeile) => zeile.normalized_name === normalizeName(item.name) && !zeile.is_checked,
      );
      if (!vorhanden) continue;

      if (action.type === "check") await setItemChecked(vorhanden.id, true);
      else await deleteItem(vorhanden.id);
      angelegt += 1;
    }
  }

  return angelegt;
}

// --- Rezepte -----------------------------------------------------------------

export async function allRecipes(): Promise<Recipe[]> {
  const rows = await db.recipes.where("family_id").equals(activeFamilyId()).toArray();
  return rows.filter((row) => row.deleted_at === null).sort((a, b) => a.name.localeCompare(b.name, "de-DE"));
}

/**
 * Die Verbindung zur Spaisekammer, oder null.
 *
 * Gelesen wie alles andere aus Dexie (Regel 1). Geschrieben wird die Tabelle
 * nie von hier: Das tun ausschließlich die Edge Functions mit der Service
 * Role, und der Pull bringt das Ergebnis zurück.
 */
export async function partnerConnection(): Promise<PartnerConnection | null> {
  const rows = await db.partner_connections.where("family_id").equals(activeFamilyId()).toArray();
  return rows.find((row) => row.deleted_at === null && row.provider === "spaisekammer") ?? null;
}

export async function recipeById(id: string): Promise<Recipe | null> {
  const treffer = await db.recipes.get(id);
  return treffer && treffer.deleted_at === null ? treffer : null;
}

export async function ingredientsOfRecipe(recipeId: string): Promise<RecipeIngredient[]> {
  const rows = await db.recipe_ingredients.where("recipe_id").equals(recipeId).toArray();
  return rows.filter((row) => row.deleted_at === null).sort((a, b) => a.sort_index - b.sort_index);
}

/** Wie viele Zutaten je Rezept, für die Zeile in der Übersicht. */
export async function ingredientCounts(): Promise<Map<string, number>> {
  const rows = await db.recipe_ingredients.where("family_id").equals(activeFamilyId()).toArray();
  const karte = new Map<string, number>();
  for (const zutat of rows) {
    if (zutat.deleted_at !== null) continue;
    karte.set(zutat.recipe_id, (karte.get(zutat.recipe_id) ?? 0) + 1);
  }
  return karte;
}

export async function createRecipe(name: string, servings = 2): Promise<Recipe> {
  const timestamp = nowIso();
  const recipe: Recipe = {
    id: newId(),
    family_id: activeFamilyId(),
    name: name.trim(),
    normalized_name: normalizeName(name),
    servings,
    note: null,
    source_url: null,
    created_by: activeUserId(),
    source: "eigen",
    external_id: null,
    external_url: null,
    external_updated_at: null,
    owner_name: null,
    cover_url: null,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("recipes", recipe);
  return recipe;
}

export async function updateRecipe(
  id: string,
  patch: Partial<Pick<Recipe, "name" | "servings" | "note" | "source_url">>,
): Promise<void> {
  const current = await db.recipes.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("recipes", {
    ...current,
    ...patch,
    normalized_name: patch.name === undefined ? current.normalized_name : normalizeName(patch.name),
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

/**
 * Rezept löschen: Grabstein für das Rezept und für jede Zutat.
 *
 * Die Datenbank räumt die Zutaten per Fremdschlüssel mit weg, aber erst beim
 * echten Löschen. Ein Grabstein ist kein Löschen, deshalb muss jede Zutat
 * ihren eigenen bekommen, sonst tauchen sie auf dem zweiten Gerät wieder auf.
 */
export async function deleteRecipe(id: string): Promise<void> {
  const timestamp = nowIso();

  for (const zutat of await ingredientsOfRecipe(id)) {
    await writeWithOutbox("recipe_ingredients", { ...zutat, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
  }
  for (const eintrag of await db.meal_plan_entries.where("recipe_id").equals(id).toArray()) {
    if (eintrag.deleted_at !== null) continue;
    await writeWithOutbox("meal_plan_entries", { ...eintrag, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
  }

  const recipe = await db.recipes.get(id);
  if (recipe) {
    await writeWithOutbox("recipes", { ...recipe, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
  }
}

/**
 * Zutat aus einer Eingabe wie "250 g Nudeln".
 *
 * Derselbe Parser wie in der Schnelleingabe der Liste: Wer beides bedient,
 * soll nicht zwei Schreibweisen lernen müssen.
 */
export async function addIngredient(recipeId: string, raw: string): Promise<RecipeIngredient | null> {
  const input = raw.trim();
  if (input.length === 0) return null;

  const parsed = parseItemInput(input);
  const timestamp = nowIso();
  const bisher = await ingredientsOfRecipe(recipeId);

  const zutat: RecipeIngredient = {
    id: newId(),
    family_id: activeFamilyId(),
    recipe_id: recipeId,
    // Bewusst ohne Produkt: "Mehl" in einem Rezept ist noch kein Eintrag im
    // Familienkatalog. Verbunden wird erst beim Auf-die-Liste-Setzen.
    product_id: null,
    name: parsed.name,
    normalized_name: parsed.normalizedName,
    quantity: parsed.quantity,
    unit: parsed.unit,
    category: guessCategory(parsed.name),
    is_staple: guessStaple({ name: parsed.name, quantity: parsed.quantity, unit: parsed.unit }),
    sort_index: (bisher.at(-1)?.sort_index ?? 0) + 1,
    external_id: null,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("recipe_ingredients", zutat);
  return zutat;
}

export async function updateIngredient(
  id: string,
  patch: Partial<Pick<RecipeIngredient, "name" | "quantity" | "unit" | "category" | "is_staple">>,
): Promise<void> {
  const current = await db.recipe_ingredients.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("recipe_ingredients", {
    ...current,
    ...patch,
    normalized_name: patch.name === undefined ? current.normalized_name : normalizeName(patch.name),
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

export async function deleteIngredient(id: string): Promise<void> {
  const current = await db.recipe_ingredients.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("recipe_ingredients", { ...current, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
}

/** Ein gelesenes Rezept aus der Edge Function als Rezept mit Zutaten anlegen. */
export async function createRecipeFromDraft(draft: RecipeDraft, sourceUrl: string | null = null): Promise<Recipe> {
  const timestamp = nowIso();
  const recipe: Recipe = {
    id: newId(),
    family_id: activeFamilyId(),
    name: draft.name.trim(),
    normalized_name: normalizeName(draft.name),
    servings: draft.servings,
    note: draft.note,
    source_url: sourceUrl,
    created_by: activeUserId(),
    source: "eigen",
    external_id: null,
    external_url: null,
    external_updated_at: null,
    owner_name: null,
    cover_url: null,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };
  await writeWithOutbox("recipes", recipe);

  let rang = 0;
  for (const zutat of draft.ingredients) {
    rang += 1;
    await writeWithOutbox("recipe_ingredients", {
      id: newId(),
      family_id: recipe.family_id,
      recipe_id: recipe.id,
      product_id: null,
      name: zutat.name.trim(),
      normalized_name: normalizeName(zutat.name),
      quantity: zutat.qty,
      // Was das Modell schickt, ist freier Text. Nur bekannte Einheiten und
      // Kategorien werden übernommen, der Rest fällt still weg.
      unit: alsEinheit(zutat.unit),
      category: alsKategorie(zutat.category) ?? guessCategory(zutat.name),
      // Das Modell erkennt Grundzutaten, aber nicht zuverlässig. Was es
      // übersieht, holt das Grundwissen nach.
      is_staple: zutat.staple || guessStaple({ name: zutat.name, quantity: zutat.qty, unit: alsEinheit(zutat.unit) }),
      sort_index: rang,
      external_id: null,
      updated_at: timestamp,
      client_updated_at: timestamp,
      deleted_at: null,
    } satisfies RecipeIngredient);
  }

  return recipe;
}

function alsEinheit(wert: string | null): Unit | null {
  return wert !== null && (UNITS as readonly string[]).includes(wert) ? (wert as Unit) : null;
}

function alsKategorie(wert: string | null): CategoryKey | null {
  return wert !== null && wert in CATEGORY_COLORS ? (wert as CategoryKey) : null;
}

// --- Vorrat ------------------------------------------------------------------

export async function allPantryItems(): Promise<PantryItem[]> {
  const rows = await db.pantry_items.where("family_id").equals(activeFamilyId()).toArray();
  return rows.filter((row) => row.deleted_at === null).sort((a, b) => a.name.localeCompare(b.name, "de-DE"));
}

export async function lowStockItems(): Promise<LowStock[]> {
  return lowStock(await allPantryItems());
}

export async function addPantryItem(raw: string, location: PantryLocation = "vorrat"): Promise<PantryItem | null> {
  const input = raw.trim();
  if (input.length === 0) return null;

  const parsed = parseItemInput(input);
  const timestamp = nowIso();

  const zeile: PantryItem = {
    id: newId(),
    family_id: activeFamilyId(),
    product_id: null,
    name: parsed.name,
    normalized_name: parsed.normalizedName,
    quantity: parsed.quantity ?? 1,
    min_quantity: null,
    unit: parsed.unit,
    category: guessCategory(parsed.name),
    location,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("pantry_items", zeile);
  return zeile;
}

export async function updatePantryItem(
  id: string,
  patch: Partial<Pick<PantryItem, "name" | "quantity" | "min_quantity" | "unit" | "category" | "location">>,
): Promise<void> {
  const current = await db.pantry_items.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("pantry_items", {
    ...current,
    ...patch,
    quantity: Math.max(0, patch.quantity ?? current.quantity),
    normalized_name: patch.name === undefined ? current.normalized_name : normalizeName(patch.name),
    updated_at: timestamp,
    client_updated_at: timestamp,
  });
}

export async function deletePantryItem(id: string): Promise<void> {
  const current = await db.pantry_items.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("pantry_items", { ...current, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
}

/** Einen Schritt mehr oder weniger. Der Knopf im Vorrat hängt daran. */
export async function adjustPantryItem(id: string, delta: number): Promise<void> {
  const current = await db.pantry_items.get(id);
  if (!current) return;
  await updatePantryItem(id, { quantity: Math.max(0, current.quantity + delta) });
}

// --- Wochenplan --------------------------------------------------------------

export async function mealPlanOfWeek(days: readonly string[]): Promise<MealPlanEntry[]> {
  const rows = await db.meal_plan_entries.where("family_id").equals(activeFamilyId()).toArray();
  return rows
    .filter((row) => row.deleted_at === null && days.includes(row.day))
    .sort((a, b) => (a.day === b.day ? a.sort_index - b.sort_index : a.day.localeCompare(b.day)));
}

export async function addMealPlanEntry(
  day: string,
  inhalt: { recipeId?: string | null; note?: string | null; servings?: number | null },
): Promise<MealPlanEntry | null> {
  const recipeId = inhalt.recipeId ?? null;
  const note = inhalt.note?.trim() ?? null;
  if (recipeId === null && (note === null || note.length === 0)) return null;

  const timestamp = nowIso();
  const amTag = (await mealPlanOfWeek([day])).length;

  const eintrag: MealPlanEntry = {
    id: newId(),
    family_id: activeFamilyId(),
    day,
    recipe_id: recipeId,
    note,
    servings: inhalt.servings ?? null,
    sort_index: amTag,
    updated_at: timestamp,
    client_updated_at: timestamp,
    deleted_at: null,
  };

  await writeWithOutbox("meal_plan_entries", eintrag);
  return eintrag;
}

export async function deleteMealPlanEntry(id: string): Promise<void> {
  const current = await db.meal_plan_entries.get(id);
  if (!current) return;

  const timestamp = nowIso();
  await writeWithOutbox("meal_plan_entries", { ...current, deleted_at: timestamp, updated_at: timestamp, client_updated_at: timestamp });
}

/**
 * Was die Woche auf der Liste bräuchte, ohne etwas zu schreiben.
 *
 * Getrennt vom Anlegen, weil niemand eine Liste von zwanzig Artikeln
 * hingeworfen bekommen will, ohne sie vorher gesehen zu haben.
 */
export async function previewWeek(days: readonly string[]): Promise<WeekPlanResult> {
  return planWeekToList({
    entries: await mealPlanOfWeek(days),
    recipes: await allRecipes(),
    ingredients: await db.recipe_ingredients.where("family_id").equals(activeFamilyId()).toArray(),
    pantry: await allPantryItems(),
    days,
  });
}

/** Dieselbe Vorschau für ein einzelnes Rezept, unabhängig vom Wochenplan. */
export async function previewRecipe(recipeId: string, servings: number | null = null): Promise<WeekPlanResult> {
  const recipe = await recipeById(recipeId);
  if (!recipe) return { missing: [], covered: [], staples: [] };

  const tag = "1970-01-01";
  return planWeekToList({
    entries: [
      {
        id: "vorschau",
        family_id: recipe.family_id,
        day: tag,
        recipe_id: recipe.id,
        note: null,
        servings,
        sort_index: 0,
        updated_at: recipe.updated_at,
        client_updated_at: recipe.client_updated_at,
        deleted_at: null,
      },
    ],
    recipes: [recipe],
    ingredients: await ingredientsOfRecipe(recipeId),
    pantry: await allPantryItems(),
    days: [tag],
  });
}

/**
 * Legt die fehlenden Zeilen einer Vorschau auf einer Liste an.
 *
 * Über addItem, damit Katalog, Kategorie und Produktverknüpfung genauso
 * entstehen wie bei getippten Artikeln. Danach werden Menge und Einheit aus
 * der Planung nachgetragen, die weiß mehr als der Parser.
 */
export async function planToList(plan: WeekPlanResult, listId: string): Promise<number> {
  let angelegt = 0;

  for (const zeile of plan.missing) {
    const item = await addItem(listId, zeile.name);
    if (!item) continue;

    await updateItem(item.id, {
      quantity: zeile.quantity,
      unit: zeile.unit,
      category: zeile.category ?? item.category,
      note: zeile.recipes.length > 0 ? zeile.recipes.join(", ") : item.note,
    });
    angelegt += 1;
  }

  return angelegt;
}

/**
 * Eine einzelne Zutat aus einem Rezept auf die Liste, mit einem Tipp.
 *
 * Der schnelle Weg neben "Zutaten auf die Liste": Wer im Rezept steht und
 * merkt, dass genau eine Sache fehlt, will kein Blatt mit Auswahl, sondern
 * einen Warenkorb an der Zeile.
 *
 * Über addItem, damit Katalog, Kategorie und Produktverknüpfung genauso
 * entstehen wie bei einem getippten Artikel. Menge, Einheit und Warengruppe
 * kommen danach aus der Zutat, die weiß mehr als der Parser. Die Notiz nennt
 * das Rezept, damit im Laden klar ist, wofür die Zeile da ist.
 *
 * Gibt null zurück, wenn dieselbe Ware schon offen auf der Liste liegt. Zwei
 * Zeilen "Milch" helfen niemandem, und der Knopf zeigt das vorher an.
 */
export async function ingredientToList(ingredientId: string, listId: string): Promise<Item | null> {
  const zutat = await db.recipe_ingredients.get(ingredientId);
  if (!zutat || zutat.deleted_at !== null) return null;

  const schon = (await itemsOfList(listId)).some(
    (eintrag) => !eintrag.is_checked && eintrag.normalized_name === zutat.normalized_name,
  );
  if (schon) return null;

  const item = await addItem(listId, zutat.name);
  if (!item) return null;

  const rezept = await db.recipes.get(zutat.recipe_id);
  await updateItem(item.id, {
    quantity: zutat.quantity ?? item.quantity,
    unit: zutat.unit ?? item.unit,
    category: zutat.category ?? item.category,
    note: rezept ? rezept.name : item.note,
  });

  return item;
}

/**
 * Dasselbe, aber jede Zeile in die Liste, in die sie gehört.
 *
 * Die gewählte Liste ist die Basis. Abweichend einsortiert wird nur, wenn sie
 * die Standardliste ist: Wer im Blatt ausdrücklich "Asiamarkt" wählt, will
 * alles dort haben, auch die Milch. Dieselbe Regel wie bei der
 * Schnelleingabe, damit es eine Regel bleibt und nicht zwei.
 */
export async function planToListSortiert(plan: WeekPlanResult, listId: string): Promise<{ angelegt: number; verteilt: { name: string; liste: string }[] }> {
  const listen = await allLists();
  const ziel = listen.find((liste) => liste.id === listId) ?? null;
  if (ziel?.is_default !== true) return { angelegt: await planToList(plan, listId), verteilt: [] };

  const laeden = await allStores();
  let angelegt = 0;
  const verteilt: { name: string; liste: string }[] = [];

  for (const zeile of plan.missing) {
    const shop = guessShop({ name: zeile.name, category: zeile.category });
    const andere = listeFuerLaden(shop, listen, laeden);
    const zielId = andere?.id ?? listId;

    const item = await addItem(zielId, zeile.name);
    if (!item) continue;

    await updateItem(item.id, {
      quantity: zeile.quantity,
      unit: zeile.unit,
      category: zeile.category ?? item.category,
      note: zeile.recipes.length > 0 ? zeile.recipes.join(", ") : item.note,
    });
    angelegt += 1;
    if (andere && andere.id !== listId) verteilt.push({ name: zeile.name, liste: andere.name });
  }

  return { angelegt, verteilt };
}

/** Was zur Neige geht, auf die Liste. Die Menge ist das, was bis zum Mindestbestand fehlt. */
export async function lowStockToList(listId: string): Promise<number> {
  let angelegt = 0;

  for (const eintrag of await lowStockItems()) {
    const item = await addItem(listId, eintrag.item.name);
    if (!item) continue;

    await updateItem(item.id, {
      quantity: eintrag.missing > 0 ? eintrag.missing : item.quantity,
      unit: eintrag.item.unit ?? item.unit,
      category: eintrag.item.category ?? item.category,
    });
    angelegt += 1;
  }

  return angelegt;
}
